import React, { useEffect, useRef, useState } from "react";
import { ZoomIn } from "lucide-react";
import { LazyImage } from "./LazyImage";

// Detects "fine pointer" (mouse) vs touch. Desktop = lens magnifier.
// Mobile = native pinch + double-tap toggle zoom.
const useFinePointer = () => {
  const [fine, setFine] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const handler = (e) => setFine(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return fine;
};

// Lens magnifier for desktop. Renders a circular div with a magnified bg-image
// positioned at the cursor.
const LensMagnifier = ({ src, alt }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 0, h: 0 });

  const LENS = 180; // px diameter of lens
  const ZOOM = 2.6;

  const onMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < 0 || y < 0 || x > r.width || y > r.height) {
      setActive(false);
      return;
    }
    setSize({ w: r.width, h: r.height });
    setPos({ x, y });
    setActive(true);
  };

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMove}
      onMouseEnter={onMove}
      onMouseLeave={() => setActive(false)}
      data-testid="zoom-image-desktop"
      className="relative w-full h-full overflow-hidden rounded-2xl bg-white/40 cursor-zoom-in"
    >
      <LazyImage
        src={src}
        alt={alt}
        wrapperClassName="w-full h-full"
        className="absolute inset-0 w-full h-full object-contain"
      />
      {/* hidden ref image kept for backround-image url */}
      <img
        ref={imgRef}
        src={src}
        alt=""
        className="hidden"
        aria-hidden="true"
      />
      {/* lens */}
      {active && size.w > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-full border-2 border-[#E0B53C]/80 shadow-[0_8px_32px_rgba(0,0,0,0.35),inset_0_0_0_2px_rgba(255,255,255,0.4)]"
          style={{
            width: LENS,
            height: LENS,
            top: pos.y - LENS / 2,
            left: pos.x - LENS / 2,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${size.w * ZOOM}px ${size.h * ZOOM}px`,
            backgroundPosition: `${-pos.x * ZOOM + LENS / 2}px ${-pos.y * ZOOM + LENS / 2}px`,
          }}
        />
      )}
      {/* tiny hint */}
      <div className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1">
        <ZoomIn size={12} strokeWidth={1.8} />
        Hover to zoom
      </div>
    </div>
  );
};

// Mobile zoom: native pinch via `touch-action: pinch-zoom`, plus double-tap
// to toggle a 2x CSS transform centred on the tap point.
const TouchZoom = ({ src, alt }) => {
  const wrapRef = useRef(null);
  const lastTapRef = useRef(0);
  const [zoom, setZoom] = useState({ scale: 1, ox: 50, oy: 50 });

  const handleTap = (e) => {
    const now = Date.now();
    const t = e.changedTouches?.[0];
    if (!t) return;
    if (now - lastTapRef.current < 280) {
      // double-tap
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const ox = ((t.clientX - r.left) / r.width) * 100;
      const oy = ((t.clientY - r.top) / r.height) * 100;
      setZoom((z) =>
        z.scale > 1 ? { scale: 1, ox: 50, oy: 50 } : { scale: 2, ox, oy },
      );
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  return (
    <div
      ref={wrapRef}
      onTouchEnd={handleTap}
      data-testid="zoom-image-mobile"
      className="relative w-full h-full overflow-hidden rounded-2xl bg-white/40"
      style={{ touchAction: "pinch-zoom" }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 ease-out will-change-transform"
        style={{
          transform: `scale(${zoom.scale})`,
          transformOrigin: `${zoom.ox}% ${zoom.oy}%`,
        }}
      />
      <div className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1">
        <ZoomIn size={12} strokeWidth={1.8} />
        Pinch · Double-tap
      </div>
    </div>
  );
};

export const ZoomableImage = ({ src, alt }) => {
  const fine = useFinePointer();
  if (!src) return null;
  return fine ? (
    <LensMagnifier src={src} alt={alt} />
  ) : (
    <TouchZoom src={src} alt={alt} />
  );
};
